import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import { getMyMustRead, dismissMustReadBanner } from "helpers/mustReadApi"

export const MustReadContext = createContext({
  data: null,
  loading: false,
  refresh: async () => {},
  dismissBanner: async () => {},
})

export const isMustReadPath = (pathname = "") => pathname === "/must-read" || pathname.startsWith("/must-read/")

export function MustReadProvider({ children }) {
  const { user } = useSelector((state) => state.Login)
  const isReviewer = [3, 4].includes(Number(user?.role))
  const location = useLocation()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const wasOnMustRead = useRef(isMustReadPath(location.pathname))

  const refresh = useCallback(async () => {
    if (!isReviewer) return
    setLoading(true)
    try {
      setData(await getMyMustRead())
    } catch (e) {
      console.error("Must Read fetch failed", e)
    } finally {
      setLoading(false)
    }
  }, [isReviewer])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    const onMustRead = isMustReadPath(location.pathname)
    if (onMustRead !== wasOnMustRead.current) refresh()
    wasOnMustRead.current = onMustRead
  }, [location.pathname, refresh])

  const dismissBanner = useCallback(async () => {
    setData((prev) => (prev ? { ...prev, bannerVisible: false } : prev))
    try {
      await dismissMustReadBanner()
    } catch (e) {
      console.error("Must Read dismiss failed", e)
    }
  }, [])

  const value = useMemo(() => ({ data, loading, refresh, dismissBanner }), [data, loading, refresh, dismissBanner])
  return <MustReadContext.Provider value={value}>{children}</MustReadContext.Provider>
}

export const useMustRead = () => useContext(MustReadContext)
